import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  buildLocalizedPath,
  stripLangPrefix,
  LANGUAGE_STORAGE_KEY,
  STANDALONE_ROUTES,
} from "../constants/languages";

// Checked whenever the visitor lands on an unprefixed (English) URL — on the
// very first page load, and again on any later navigation back to a bare
// path (e.g. a bookmark or typed URL). Already-prefixed URLs short-circuit
// immediately below, so this is a cheap no-op except when a redirect is
// actually needed:
//  - a remembered explicit choice (set only by the language switcher) wins, and
//    auto-detection never runs again once that choice exists
//  - otherwise, check the browser's language; redirect if it's supported
//  - if neither applies, stay on English
const LanguageRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const { pathname, search } = location;

    // Standalone routes (e.g. /admin, /portal) live outside the language-prefix
    // system entirely — there's no "/tr/portal" to redirect to, so leave them alone.
    if (STANDALONE_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`))) {
      return;
    }

    // Already on a language-prefixed URL — nothing to do here.
    if (stripLangPrefix(pathname) !== pathname) return;

    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      if (stored !== DEFAULT_LANGUAGE && isSupportedLanguage(stored)) {
        navigate(`${buildLocalizedPath(pathname, stored)}${search}`, { replace: true });
      }
      return;
    }

    const browserLangs = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
    const match = browserLangs
      .map((l) => l.split("-")[0])
      .find((l) => l !== DEFAULT_LANGUAGE && isSupportedLanguage(l));

    if (match) {
      navigate(`${buildLocalizedPath(pathname, match)}${search}`, { replace: true });
    }
  }, [location, navigate]);

  return null;
};

export default LanguageRedirect;
